import React from 'react';
import { Link } from 'react-router';
import Header from './Header';
import Table from '@material-table/core';
import auth from './auth';
import {
  getPortalUsers,
  deletePortalUser as _deletePortalUser,
  PortalUser,
} from './ajax';
import { Typography } from '@mui/material';
import { toast } from 'react-hot-toast';
import { useQuery } from '@tanstack/react-query';
import { PrimaryButton, SecondaryButton } from '@client/new/components/button';
import { pageBackground } from '@client/new/pages/page-background.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faTrash } from '@fortawesome/pro-regular-svg-icons';

function usePortalUsers() {
  const { data, refetch } = useQuery(['admin', 'portalUsers'], () =>
    getPortalUsers(),
  );

  return {
    data,
    deletePortalUser(id) {
      const answer = confirm('Are you sure you want to *delete* this User?');
      if (answer) {
        _deletePortalUser(id)
          .then(() => {
            toast.success(`[PortalUserList] PortalUser ${id} deleted.`);
            refetch();
          })
          .catch((e) => {
            toast.error(`[PortalUserList] Error deleting ${id} PortalUser.`);
          });
      }
    },
  };
}

function portalUserToTable(pu: PortalUser) {
  const ROLE_MAP = {
    1: 'Super Admin',
    2: 'Admin',
    3: 'Creator',
    4: 'Content Editor',
  };

  return {
    id: pu.id,
    name: pu.firstName + ' ' + pu.lastName,
    email: pu.email,
    isActive: pu.isActive ? 'Active' : 'Disabled',
    influencerId: pu.influencerId,
    role: ROLE_MAP[pu.role],
  };
}

function PortalUserListPage() {
  const { data, deletePortalUser } = usePortalUsers();

  const url = new URL(window.location.href);
  const q = url.searchParams.get('q');
  const displayedData = (data ?? []).filter((pu) =>
    q ? pu.email.includes(q) : true,
  );

  const columns = [
    {
      title: 'Name',
      field: 'name',
    },
    {
      title: 'Email',
      field: 'email',
      render: (row) => <span className="select-all">{row.email}</span>,
    },
    {
      title: 'Active',
      field: 'isActive',
    },
    {
      title: 'Creator ID',
      field: 'influencerId',
    },
    {
      title: 'Role',
      field: 'role',
    },
    {
      title: 'Actions',
      render: (row) => (
        <div>
          {auth.isAdministrator() ? (
            <div className="flex gap-10">
              <Link to={'/admin/portal-users/' + row.id + '/edit'}>
                <FontAwesomeIcon icon={faEdit} className="cursor-pointer" />
              </Link>
              <a
                id="delete-user"
                data-email={row.email}
                className=""
                onClick={() => {
                  deletePortalUser(row.id);
                }}
              >
                <FontAwesomeIcon
                  icon={faTrash}
                  className="cursor-pointer"
                  color="red"
                />
              </a>
            </div>
          ) : null}
        </div>
      ),
    },
  ];

  return (
    <div className={pageBackground}>
      <Header>
        <Typography variant="h4">Portal users</Typography>
        <div className="flex gap-1 md:gap-3 flex-row">
          {auth.isAdministrator() || auth.isModerator() ? (
            <div className="button-box">
              <Link to="/admin/portal-users/0/edit">
                <PrimaryButton>Create new User</PrimaryButton>
              </Link>
            </div>
          ) : null}
        </div>
      </Header>
      <Table
        title="Portal Users"
        columns={columns}
        data={displayedData.map(portalUserToTable)}
        options={{ pageSize: 10, filtering: true }}
      />
    </div>
  );
}

export default PortalUserListPage;
